import { useNavigate } from '@tanstack/react-router';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { CommandRowIcon } from './CommandRowIcon';
import { findCommandPage, type CommandPageItem } from './command-palette-registry';

/**
 * Which half of the page results to render. The palette renders this component
 * TWICE so that "Quick actions" can sit between the two — `'recent'` above it,
 * `'rest'` below (see `CommandPalette.tsx`'s `CommandList`). Splitting on a prop
 * rather than into two components keeps `select` / `PageItemContent` /
 * `commandItemValue` in one place, since both halves render the same row.
 */
export type PageResultsSection = 'recent' | 'rest';

type Props = {
  query: string;
  recentIds: string[];
  /** Already role/plan-filtered — computed once in `CommandPalette.tsx` so
   * the result-count bar and this component read the exact same list. */
  visiblePages: CommandPageItem[];
  section: PageResultsSection;
  onSelectPage: (page: CommandPageItem) => void;
};

// Order groups read in when the query is empty — the breadcrumb's top
// segment ("Workspace › Email Accounts" → "Workspace"). Any segment this
// list doesn't know about (there shouldn't be one) still renders, just after
// these three.
const TOP_SEGMENT_ORDER = ['Workspace', 'Account', 'Organization'];

/**
 * The palette's page results.
 *
 * Empty query: "Recent" (if any pages have been visited via the palette
 * before) followed by every visible page, grouped by the top segment of its
 * breadcrumb — never just "Quick actions" alone, which is what a fresh
 * viewer with no recent history saw before this fix.
 *
 * Typed query: a single cmdk-filtered "Pages" group (cmdk hides non-matching
 * items/groups itself; a per-section split isn't useful once the list is
 * already narrowed by the query).
 */
export function PageResults({ query, recentIds, visiblePages, section, onSelectPage }: Props) {
  const navigate = useNavigate();
  const hasQuery = query.trim().length > 0;

  function select(page: CommandPageItem) {
    page.go(navigate);
    onSelectPage(page);
  }

  if (section === 'recent') {
    // "Recent" belongs to the empty-query screen only — once a query narrows
    // the list, cmdk's own filtering is the ordering that matters.
    if (hasQuery) return null;

    const visibleIds = new Set(visiblePages.map((p) => p.id));
    const recentPages = recentIds
      .map((id) => findCommandPage(id))
      .filter((p): p is CommandPageItem => p !== undefined && visibleIds.has(p.id));
    if (recentPages.length === 0) return null;

    return (
      <CommandGroup heading="Recent">
        {recentPages.map((page) => (
          <CommandItem
            key={`recent-${page.id}`}
            value={recentItemValue(page)}
            keywords={[page.route, ...(page.keywords ?? [])]}
            onSelect={() => select(page)}
          >
            <PageItemContent page={page} />
          </CommandItem>
        ))}
      </CommandGroup>
    );
  }

  if (hasQuery) {
    return (
      <CommandGroup heading="Pages">
        {visiblePages.map((page) => (
          <CommandItem
            key={page.id}
            value={commandItemValue(page)}
            keywords={[page.route, ...(page.keywords ?? [])]}
            onSelect={() => select(page)}
          >
            <PageItemContent page={page} />
          </CommandItem>
        ))}
      </CommandGroup>
    );
  }

  return (
    <>
      {groupPagesBySegment(visiblePages).map(([segment, pages]) => (
        <CommandGroup key={segment} heading={segment}>
          {pages.map((page) => (
            <CommandItem
              key={page.id}
              value={commandItemValue(page)}
              keywords={[page.route, ...(page.keywords ?? [])]}
              onSelect={() => select(page)}
            >
              <PageItemContent page={page} />
            </CommandItem>
          ))}
        </CommandGroup>
      ))}
    </>
  );
}

function PageItemContent({ page }: { page: CommandPageItem }) {
  return (
    <>
      <CommandRowIcon icon={page.icon} />
      <div className="flex min-w-0 flex-col">
        <span className="truncate">{page.label}</span>
        {/* 11.5px, matching the sidebar/`/api-keys` breadcrumb-style muted
            text scale rather than the 12px `text-xs` default. */}
        <span className="truncate text-[11.5px] text-muted-foreground">{page.breadcrumb}</span>
      </div>
    </>
  );
}

/**
 * cmdk requires a UNIQUE `value` per item (its selection-highlight state is
 * keyed by this string) — a bare `page.label` collides for "Notifications"
 * (the top-level inbox feed vs. the Settings → Notifications preferences
 * section share that title). Folding the breadcrumb in keeps it unique
 * without weakening the match: it's also exactly the secondary text a typed
 * query like "settings notifications" should hit.
 */
function commandItemValue(page: CommandPageItem): string {
  return `${page.label} ${page.breadcrumb}`;
}

/**
 * Same page can appear twice on the empty-query screen — once under
 * "Recent", once in its section group below — so the Recent row needs a
 * value distinct from the section-group row's, on top of being distinct
 * from every OTHER Recent row.
 */
function recentItemValue(page: CommandPageItem): string {
  return `recent ${commandItemValue(page)}`;
}

function groupPagesBySegment(pages: CommandPageItem[]): Array<[string, CommandPageItem[]]> {
  const groups = new Map<string, CommandPageItem[]>();
  for (const page of pages) {
    const segment = page.breadcrumb.split(' › ')[0] ?? page.breadcrumb;
    const list = groups.get(segment);
    if (list) {
      list.push(page);
    } else {
      groups.set(segment, [page]);
    }
  }

  const known = TOP_SEGMENT_ORDER.filter((segment) => groups.has(segment)).map(
    (segment): [string, CommandPageItem[]] => [segment, groups.get(segment) as CommandPageItem[]],
  );
  const rest = [...groups.entries()].filter(([segment]) => !TOP_SEGMENT_ORDER.includes(segment));
  return [...known, ...rest];
}
